import React from 'react';

import { PlusOutlinedIcon, UpdateIcon } from '../../assets/images/icons';
import artemisadsLogoUrl from '../../assets/images/logos/attribution/artemisads-icon.png';
import blueaffLogoUrl from '../../assets/images/logos/attribution/blueaff-icon.png';
import levantaLogoUrl from '../../assets/images/logos/attribution/levanta-icon.png';
import partnerboostLogoUrl from '../../assets/images/logos/attribution/partnerboost-icon.png';

export enum AttributionLinkSource {
  PartnerBoost = 'partnerboost',
  Levanta = 'levanta',
  ArtemisAds = 'artemisads',
  BlueAFF = 'blueaff',
}

export const HARDCODED_LINK_SOURCE_MANUAL = 'manual' as const;

export type HardcodedLinkSourceValue = AttributionLinkSource | typeof HARDCODED_LINK_SOURCE_MANUAL;

export const ATTRIBUTION_SOURCE_LABELS: Record<AttributionLinkSource, string> = {
  [AttributionLinkSource.PartnerBoost]: 'PartnerBoost',
  [AttributionLinkSource.Levanta]: 'Levanta',
  [AttributionLinkSource.ArtemisAds]: 'ArtemisAds',
  [AttributionLinkSource.BlueAFF]: 'BlueAFF',
};

export const ATTRIBUTION_SOURCE_LOGOS: Record<AttributionLinkSource, string> = {
  [AttributionLinkSource.PartnerBoost]: partnerboostLogoUrl,
  [AttributionLinkSource.Levanta]: levantaLogoUrl,
  [AttributionLinkSource.ArtemisAds]: artemisadsLogoUrl,
  [AttributionLinkSource.BlueAFF]: blueaffLogoUrl,
};

export interface AttributionSourceOption {
  label: string;
  value: AttributionLinkSource;
}

export const ATTRIBUTION_SOURCE_OPTIONS: AttributionSourceOption[] = [
  { label: ATTRIBUTION_SOURCE_LABELS[AttributionLinkSource.PartnerBoost], value: AttributionLinkSource.PartnerBoost },
  { label: ATTRIBUTION_SOURCE_LABELS[AttributionLinkSource.Levanta], value: AttributionLinkSource.Levanta },
  { label: ATTRIBUTION_SOURCE_LABELS[AttributionLinkSource.ArtemisAds], value: AttributionLinkSource.ArtemisAds },
  { label: ATTRIBUTION_SOURCE_LABELS[AttributionLinkSource.BlueAFF], value: AttributionLinkSource.BlueAFF },
];

const ATTRIBUTION_SOURCE_VALUES = new Set<string>(Object.values(AttributionLinkSource));

export const isAttributionLinkSource = (value: string | null | undefined): value is AttributionLinkSource =>
  !!value && ATTRIBUTION_SOURCE_VALUES.has(value);

export const getHardcodedLinkSourceLabel = (source?: string | null): string | null => {
  if (source === HARDCODED_LINK_SOURCE_MANUAL) {
    return 'Manual';
  }
  if (isAttributionLinkSource(source)) {
    return ATTRIBUTION_SOURCE_LABELS[source];
  }
  return null;
};

export const getHardcodedLinkSourceIcon = (source?: string | null): string | null => {
  if (isAttributionLinkSource(source)) {
    return ATTRIBUTION_SOURCE_LOGOS[source];
  }
  return null;
};

interface AttributionLinkButtonIconProps {
  className?: string;
  isHardcoded: boolean;
  source?: string | null;
}

export const AttributionLinkButtonIcon = ({
  className,
  isHardcoded,
  source,
}: AttributionLinkButtonIconProps): JSX.Element => {
  if (!isHardcoded) {
    return <PlusOutlinedIcon />;
  }

  const iconUrl = getHardcodedLinkSourceIcon(source);
  if (iconUrl) {
    return (
      <img
        alt={getHardcodedLinkSourceLabel(source) ?? ''}
        className={className}
        src={iconUrl}
      />
    );
  }

  return <UpdateIcon />;
};

export const formatAttributionSourceOptionLabel = (option: AttributionSourceOption) => (
  <div style={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
    <img
      alt={option.label}
      src={ATTRIBUTION_SOURCE_LOGOS[option.value]}
      style={{ height: 'auto', width: '16px' }}
    />
    <span>{option.label}</span>
  </div>
);

interface AttributionSourceBadgeProps {
  source?: AttributionLinkSource | string | null;
  iconClassName?: string;
  labelClassName?: string;
  iconOnly?: boolean;
}

export const AttributionSourceBadge = ({
  iconClassName,
  iconOnly = false,
  labelClassName,
  source,
}: AttributionSourceBadgeProps): JSX.Element => {
  if (!isAttributionLinkSource(source)) {
    return <span>-</span>;
  }

  return (
    <span style={{ alignItems: 'center', display: 'inline-flex', gap: iconOnly ? 0 : '8px' }}>
      <img
        alt={ATTRIBUTION_SOURCE_LABELS[source]}
        className={iconClassName}
        src={ATTRIBUTION_SOURCE_LOGOS[source]}
      />
      {!iconOnly && (
        <span className={labelClassName}>{ATTRIBUTION_SOURCE_LABELS[source]}</span>
      )}
    </span>
  );
};
